import React, { useRef, useState } from "react";
import { MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { InquiryFields } from "../components/InquiryFields";
import { InquirySuccess } from "../components/InquirySuccess";
import { useApp } from "../context/AppContext";
import { api } from "../api";

const blank = { name: "", phone: "", senderCountry: "", receiverCountry: "", contents: "", weight: "", message: "" };

const InquiryForm = () => {
  const { user, t } = useApp();
  const initial = () => ({ ...blank, name: user?.name || "", phone: user?.phone || "" });
  const [form, setForm] = useState(initial);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError("");
    if (!form.name.trim() || !form.senderCountry || !form.receiverCountry) { setError(t("inquiry.required")); return; }
    const digits = form.phone.replace(/\D/g, "");
    if (!/^\+?[0-9 ()-]+$/.test(form.phone.trim()) || digits.length < 7 || digits.length > 15) { setError(t("inquiry.invalidPhone")); return; }
    submitting.current = true;
    setBusy(true);
    // Open synchronously to avoid popup blockers; navigate only after database save.
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    try {
      const saved = await api.addInquiry({ ...form, weight: form.weight === "" ? null : Number(form.weight) });
      setReceipt(saved);
      if (popup && !popup.closed && saved.whatsappUrl) popup.location.replace(saved.whatsappUrl);
      else if (popup && !popup.closed) popup.close();
    } catch (err) {
      if (popup) popup.close();
      setError(t("inquiry.failed"));
    } finally { setBusy(false); submitting.current = false; }
  };
  if (receipt) return <InquirySuccess receipt={receipt} onAnother={() => { setReceipt(null); setForm(initial()); }} />;
  return <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16 animate-fade-up">
    <div className="flex items-center gap-2 text-orange-600 mb-4"><MessageCircle className="w-6 h-6" /><span className="text-sm font-semibold">SmartLogistics</span></div>
    <h1 data-testid="inquiry-heading" className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl">{t("inquiry.title")}</h1>
    <form data-testid="inquiry-form" onSubmit={submit} className="mt-10">
      <fieldset disabled={busy} className="min-w-0"><InquiryFields form={form} setForm={setForm} /></fieldset>
      {error && <p role="alert" data-testid="inquiry-error" className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      <p data-testid="inquiry-consent" className="text-xs text-neutral-500 mt-7">{t("inquiry.consent")}</p>
      <Button type="submit" data-testid="inquiry-submit-button" disabled={busy} className="orange-gradient text-white border-0 gap-2 h-auto min-h-12 py-3 px-6 mt-5 whitespace-normal text-left hover:opacity-90">
        {busy ? <Loader2 className="w-4 h-4 shrink-0 animate-spin" /> : <MessageCircle className="w-4 h-4 shrink-0" />}{t(busy ? "inquiry.saving" : "inquiry.submit")}<ArrowUpRight className="w-4 h-4 shrink-0" />
      </Button>
    </form>
  </div>;
};

export default function Inquiry() {
  const { ready, t } = useApp();
  return ready ? <InquiryForm /> : <p data-testid="inquiry-loading" role="status" className="max-w-3xl mx-auto px-5 py-16">{t("inquiry.loading")}</p>;
}